import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { 
  FileImage, 
  Download,
  Printer,
  ChevronLeft,
  ChevronRight,
  Trash2,
  X,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

export const DocumentViewerPage = () => {
  const { reportId, stopId } = useParams();
  const navigate = useNavigate();
  const { get, del } = useApi();
  
  const [documents, setDocuments] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, stopId]);

  const fetchDocuments = async () => {
    try {
      const result = await get(`/daily-reports/${reportId}/stops/${stopId}/documents`);
      if (result.success) {
        setDocuments(result.data);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = async (docId) => {
    try {
      const result = await del(`/documents/${docId}`);
      if (result.success) {
        setDocuments(prev => prev.filter(d => d.id !== docId));
        if (currentIndex >= documents.length - 1) {
          setCurrentIndex(Math.max(0, currentIndex - 1));
        }
        toast.success('Document supprimé');
      }
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const printDocument = () => {
    const currentDoc = documents[currentIndex];
    if (!currentDoc) return;

    // Create a new window for printing at letter size
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Document - ${currentDoc.filename || 'Scan'}</title>
          <style>
            @page {
              size: letter;
              margin: 0.5in;
            }
            body {
              margin: 0;
              padding: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
            }
            img {
              max-width: 100%;
              max-height: 100%;
              object-fit: contain;
            }
          </style>
        </head>
        <body>
          <img src="${currentDoc.image_base64}" alt="Document" />
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const downloadDocument = () => {
    const currentDoc = documents[currentIndex];
    if (!currentDoc) return;

    const link = document.createElement('a');
    link.href = currentDoc.image_base64;
    link.download = currentDoc.filename || `document-${currentIndex + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const goBack = () => {
    navigate(`/reports/${reportId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
        <FileImage className="text-slate-600 mb-4" size={64} />
        <p className="text-slate-400 text-lg mb-4">Aucun document</p>
        <Button onClick={goBack} variant="outline" className="border-slate-700">
          <ChevronLeft size={20} className="mr-2" />
          Retour au rapport
        </Button>
      </div>
    );
  }

  const currentDoc = documents[currentIndex];

  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="document-viewer">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-950 border-b border-slate-800">
        <Button onClick={goBack} variant="ghost" className="text-white">
          <X size={24} />
        </Button>
        
        <span className="text-white font-medium">
          {currentIndex + 1} / {documents.length}
        </span>
        
        <div className="flex gap-2">
          <Button 
            onClick={printDocument} 
            variant="ghost" 
            className="text-white"
            data-testid="print-btn"
          >
            <Printer size={24} />
          </Button>
          <Button 
            onClick={downloadDocument} 
            variant="ghost" 
            className="text-white"
            data-testid="download-btn"
          >
            <Download size={24} />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="text-red-500">
                <Trash2 size={24} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-slate-900 border-slate-800">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">Supprimer ce document?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">
                  Cette action est irréversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white">
                  Annuler
                </AlertDialogCancel>
                <AlertDialogAction 
                  onClick={() => deleteDocument(currentDoc.id)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Image Viewer */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center">
        <img
          src={currentDoc.image_base64}
          alt={`Document ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain transition-transform"
          style={{ transform: `scale(${zoom})` }}
          data-testid="document-image"
        />

        {/* Navigation Arrows */}
        {documents.length > 1 && (
          <>
            <button
              onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
              disabled={currentIndex === 0}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 flex items-center justify-center disabled:opacity-30"
              data-testid="prev-btn"
            >
              <ChevronLeft className="text-white" size={28} />
            </button>
            <button
              onClick={() => setCurrentIndex(prev => Math.min(documents.length - 1, prev + 1))}
              disabled={currentIndex === documents.length - 1}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 flex items-center justify-center disabled:opacity-30"
              data-testid="next-btn"
            >
              <ChevronRight className="text-white" size={28} />
            </button>
          </>
        )}
      </div>

      {/* Zoom Controls */}
      <div className="flex items-center justify-center gap-4 p-4 bg-slate-950 border-t border-slate-800">
        <Button
          onClick={() => setZoom(prev => Math.max(0.5, prev - 0.25))}
          variant="outline"
          className="border-slate-700"
          disabled={zoom <= 0.5}
        >
          <ZoomOut size={20} />
        </Button>
        <span className="text-white font-mono w-16 text-center">{Math.round(zoom * 100)}%</span>
        <Button
          onClick={() => setZoom(prev => Math.min(3, prev + 0.25))}
          variant="outline"
          className="border-slate-700"
          disabled={zoom >= 3}
        >
          <ZoomIn size={20} />
        </Button>
      </div>

      {/* Thumbnail Strip */}
      {documents.length > 1 && (
        <div className="flex gap-2 p-4 bg-slate-950 overflow-x-auto">
          {documents.map((doc, idx) => (
            <button
              key={doc.id}
              onClick={() => setCurrentIndex(idx)}
              className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 ${
                idx === currentIndex ? 'border-amber-500' : 'border-transparent'
              }`}
            >
              <img
                src={doc.image_base64}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentViewerPage;
